// charts.jsx — SVG gauge, donut, and horizontal bars (ported from the design, V2).
import React from "react";
import { readinessBand, READINESS_BANDS } from "./data";

// ---- Semicircular gauge (0–100) with discrete readiness-band segments ----
// Each readiness band is its own coloured arc (with a small gap between them),
// the two outer tips are rounded, and a knob marker sits at the current score.
export function Gauge({ value }) {
  const R = 130, CX = 160, CY = 160;
  const band = readinessBand(value);
  const frac = Math.max(0, Math.min(100, value)) / 100;
  // indicator position along the semicircle (180° → 0°)
  const ang = Math.PI - frac * Math.PI;
  const ix = CX + R * Math.cos(ang);
  const iy = CY - R * Math.sin(ang);
  const pt = (v) => {
    const a = Math.PI - (Math.max(0, Math.min(100, v)) / 100) * Math.PI;
    return [CX + R * Math.cos(a), CY - R * Math.sin(a)];
  };
  const GAP = 1.0; // value-units of gap between segments (flat internal edges)
  const segments = READINESS_BANDS.map((b, i) => {
    const from = b.min + (i === 0 ? 0 : GAP);
    const to = b.max + (i === READINESS_BANDS.length - 1 ? 1 : 1 - GAP);
    const [x1, y1] = pt(from);
    const [x2, y2] = pt(to);
    return { d: `M ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2}`, color: b.color, key: b.label };
  });
  const [tip0x, tip0y] = pt(0);
  const [tip1x, tip1y] = pt(100);
  return (
    <svg className="es-gauge" viewBox="0 0 320 186" role="img" aria-label={"Organizational readiness index " + value + " out of 100"}>
      {segments.map((s) => (
        <path key={s.key} d={s.d} fill="none" stroke={s.color} strokeWidth="18" strokeLinecap="butt" />
      ))}
      {/* round only the two outer tips of the whole dial */}
      <circle cx={tip0x} cy={tip0y} r="9" fill={READINESS_BANDS[0].color} />
      <circle cx={tip1x} cy={tip1y} r="9" fill={READINESS_BANDS[READINESS_BANDS.length - 1].color} />
      {/* current-score indicator */}
      <circle
        cx={ix}
        cy={iy}
        r="13"
        fill="var(--vas-ink-100)"
        stroke={band.color}
        strokeWidth="5"
        style={{ transition: "cx .8s cubic-bezier(.22,1,.36,1), cy .8s cubic-bezier(.22,1,.36,1), stroke .4s ease" }}
      />
      <text x="30" y="182" fill="rgba(255,255,255,0.35)" fontSize="12" fontFamily="var(--vas-font-mono)" textAnchor="middle">0</text>
      <text x="290" y="182" fill="rgba(255,255,255,0.35)" fontSize="12" fontFamily="var(--vas-font-mono)" textAnchor="middle">100</text>
    </svg>
  );
}

// ---- Gender donut (static: no hover highlight) ----
export function Donut({ data, total }) {
  const R = 58, SW = 22, C = 70, circ = 2 * Math.PI * R;
  let offset = 0;
  const segs = data.map((d) => {
    const len = (d.pct / 100) * circ;
    const seg = { ...d, len, offset };
    offset += len;
    return seg;
  });
  return (
    <div className="es-donut">
      <svg width="140" height="140" viewBox="0 0 140 140" role="img" aria-label="Gender distribution">
        <g transform="rotate(-90 70 70)">
          <circle cx={C} cy={C} r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={SW} />
          {segs.map((s, i) => (
            <circle
              key={i}
              cx={C}
              cy={C}
              r={R}
              fill="none"
              stroke={s.color}
              strokeWidth={SW}
              strokeDasharray={s.len + " " + (circ - s.len)}
              strokeDashoffset={-s.offset}
              style={{ transition: "stroke-dasharray .8s ease-out" }}
            />
          ))}
        </g>
        <text x="70" y="66" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="700" fontFamily="DM Sans, sans-serif">
          {total != null ? total.toLocaleString("en-US") : ""}
        </text>
        <text x="70" y="84" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontFamily="DM Sans, sans-serif">
          in scope
        </text>
      </svg>
      <div className="es-donut__legend">
        {data.map((d) => (
          <div className="es-donut__item" key={d.label}>
            <span className="es-donut__sw" style={{ background: d.color }}></span>
            <span>{d.label}</span>
            <b>{d.pct}%</b>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Age horizontal bars ----
export function AgeBars({ data }) {
  const max = Math.max(...data.map((d) => d.pct), 1);
  return (
    <div className="es-bars">
      {data.map((d) => (
        <div className="es-bar" key={d.label}>
          <span className="es-bar__lbl">{d.label}</span>
          <div className="es-bar__track">
            <div className="es-bar__fill" style={{ width: (d.pct / max) * 100 + "%" }}></div>
          </div>
          <span className="es-bar__pct">{d.pct}%</span>
        </div>
      ))}
    </div>
  );
}
